import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { usePortalAuth } from "../PortalAuthContext";
import { DEFAULT_TAB, tabsForUser, type PortalTabId } from "./PortalTabMatrix";
import { PortalChrome } from "./PortalChrome";

interface PortalDashboardShellProps {
  /** Right-aligned title shown in the top bar (e.g. "Buyer dashboard"). */
  pageTitle: string;
  /** Render the active tab's content. Same shape PortalTabsShell exposed
   *  so dashboards can swap in without rewriting their renderer. */
  renderTab: (activeTab: PortalTabId) => ReactNode;
}

// Post-Rollout PR 2 (PROB-13) — the dashboard chrome replaces the
// hand-rolled horizontal tab strip with the internal-style sidebar +
// top bar + content layout. The active tab is still mirrored to ?tab=
// in the URL so back/forward + bookmarks behave as before; the only
// visible change is that tab navigation moved from a horizontal strip
// at the top to a vertical sidebar at the left.
export function PortalDashboardShell({
  pageTitle,
  renderTab,
}: PortalDashboardShellProps) {
  const auth = usePortalAuth();
  const [params, setParams] = useSearchParams();
  // SP-2b — the default landing tab is the user's FIRST tab in their
  // lineup (Buyer lands on Home; every other type's lineup still starts
  // with Requests, so their behavior is unchanged). The lineup needs
  // auth.me, so an absent ?tab= is kept as null until render resolves it.
  const initial = (params.get("tab") as PortalTabId) || null;
  const [activeTab, setActiveTab] = useState<PortalTabId | null>(initial);

  useEffect(() => {
    const fromUrl = (params.get("tab") as PortalTabId) || null;
    if (fromUrl !== activeTab) setActiveTab(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const onSelect = useCallback(
    (next: PortalTabId) => {
      setActiveTab(next);
      const updated = new URLSearchParams(params);
      updated.set("tab", next);
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  // Resolve the lineup + landing tab. Computed with a me-guard so the route-
  // guard effect below stays unconditional (rules of hooks). BrokerType hides
  // the Payment-setup tab for a JUNIOR broker.
  const me = auth.me;
  const tabs = me ? tabsForUser(me.accountType, me.kind, me.brokerType) : [];
  const requested = activeTab ?? tabs[0]?.id ?? DEFAULT_TAB;
  const visibleId = tabs.some((t) => t.id === requested)
    ? requested
    : tabs[0]?.id ?? DEFAULT_TAB;

  // Route guard — if the URL requests a tab that isn't in this user's lineup
  // (e.g. a JUNIOR broker deep-linking ?tab=payment-setup, hidden for them),
  // rewrite ?tab= to the resolved landing tab so the address bar matches what's
  // shown. A no-op for every in-lineup request (every other portal / tab).
  useEffect(() => {
    if (!me) return;
    const fromUrl = params.get("tab");
    if (fromUrl && fromUrl !== visibleId && !tabs.some((t) => t.id === fromUrl)) {
      const updated = new URLSearchParams(params);
      updated.set("tab", visibleId);
      setParams(updated, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, visibleId, me]);

  if (!me) {
    return (
      <div className="min-h-screen bg-white-warm flex items-center justify-center text-small text-gray-mid">
        Loading…
      </div>
    );
  }

  return (
    <PortalChrome pageTitle={pageTitle} activeTab={visibleId} onSelectTab={onSelect}>
      {renderTab(visibleId)}
    </PortalChrome>
  );
}
